import React from 'react';
import { createRoot } from 'react-dom/client';
import 'streamdown/styles.css';
import './assistant.css';
// Live vault events subscriber — runs once per tab, idempotent. Mounts
// /api/events/stream and exposes window.hubEvents on every hub page.
import '../hub-events.js';
import { AssistantPanel } from './AssistantPanel.jsx';
import { ChatPage } from './ChatPage.jsx';
import { DevLogPanel } from './DevLogPanel.jsx';
import { GatewayPanel } from './GatewayPanel.jsx';
import { HubToastMount } from './HubToast.jsx';
import { OpenClawBeamIndicator } from './shared.jsx';

const FAB_ID = 'hub-assistant';
const PAGE_ID = 'hub-chat-page';
const EMBED_ID = 'hub-assistant-embed';
const BEAM_ID = 'hub-openclaw-beam-root';
const DEV_LOG_ID = 'hub-dev-log-root';
const GATEWAY_FAB_ID = 'hub-gateway-root';
const TOAST_ID = 'hub-toast-root';

function isFramedWindow() {
  if (typeof window === 'undefined') return false;
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

// The floating assistant surfaces (gateway FAB, beam, legacy FAB) must not
// appear on unauthenticated pages — pre-login and pre-SSO-redirect the FAB
// was floating over a blank/login screen. /api/auth/me is 200 for a live
// session (and for standalone dev via HUB_AUTH_DISABLED), 401 otherwise.
// A failed check is not cached so a later explicit mount() re-checks;
// the SSO flow reloads the page anyway.
let hubAuthedPromise = null;
function hubAuthed() {
  if (!hubAuthedPromise) {
    hubAuthedPromise = fetch('/api/auth/me', { headers: { accept: 'application/json' } })
      .then((res) => res.ok)
      .catch(() => false)
      .then((ok) => {
        if (!ok) hubAuthedPromise = null;
        return ok;
      });
  }
  return hubAuthedPromise;
}

function mountAssistantSurfaces() {
  let beamEl = document.getElementById(BEAM_ID);
  if (!beamEl) {
    beamEl = document.createElement('div');
    beamEl.id = BEAM_ID;
    document.body.appendChild(beamEl);
  }
  if (beamEl.dataset.mounted !== '1') {
    beamEl.dataset.mounted = '1';
    createRoot(beamEl).render(<OpenClawBeamIndicator />);
  }

  let gatewayEl = document.getElementById(GATEWAY_FAB_ID);
  if (!gatewayEl) {
    gatewayEl = document.createElement('div');
    gatewayEl.id = GATEWAY_FAB_ID;
    document.body.appendChild(gatewayEl);
  }
  if (gatewayEl.dataset.mounted !== '1') {
    gatewayEl.dataset.mounted = '1';
    createRoot(gatewayEl).render(<GatewayPanel />);
  }

  // Legacy assistant FAB only mounts when v2 is explicitly disabled —
  // otherwise GatewayPanelV2 owns the floating button + the ⌘/Ctrl+I
  // shortcut. The embed-mode mount in mount() stays intact so
  // app.moltpod.com iframes still get a working panel until that lane
  // migrates too.
  const legacyFabSuppressed = (() => {
    try {
      const qs = new URLSearchParams(window.location.search);
      if (qs.get('gw-v2') === '0') return false;
      if (qs.get('gw-v2') === '1') return true;
      if (localStorage.getItem('hub-gateway-v2') === '0') return false;
    } catch {}
    return true;
  })();

  const fabEl = document.getElementById(FAB_ID);
  if (fabEl && !legacyFabSuppressed && fabEl.dataset.mounted !== '1') {
    fabEl.dataset.mounted = '1';
    createRoot(fabEl).render(<AssistantPanel />);
  }
}

function mount() {
  const framed = isFramedWindow();

  // Toast root lives on every page — framed or not — so any hub page can
  // call window.hubToast.success(...) / .error(...) etc.
  let toastEl = document.getElementById(TOAST_ID);
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.id = TOAST_ID;
    document.body.appendChild(toastEl);
  }
  if (toastEl.dataset.mounted !== '1') {
    toastEl.dataset.mounted = '1';
    createRoot(toastEl).render(<HubToastMount />);
  }

  let devLogEl = document.getElementById(DEV_LOG_ID);
  if (!devLogEl) {
    devLogEl = document.createElement('div');
    devLogEl.id = DEV_LOG_ID;
    document.body.appendChild(devLogEl);
  }
  if (devLogEl.dataset.mounted !== '1') {
    devLogEl.dataset.mounted = '1';
    createRoot(devLogEl).render(<DevLogPanel />);
  }

  if (!framed) {
    hubAuthed().then((ok) => {
      if (ok) mountAssistantSurfaces();
    });
  }

  const pageEl = document.getElementById(PAGE_ID);
  if (pageEl) {
    if (pageEl.dataset.mounted === '1') return;
    pageEl.dataset.mounted = '1';
    createRoot(pageEl).render(<ChatPage />);
  }

  const embedEl = document.getElementById(EMBED_ID);
  if (embedEl) {
    if (embedEl.dataset.mounted === '1') return;
    embedEl.dataset.mounted = '1';
    createRoot(embedEl).render(<AssistantPanel embedded />);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mount);
} else {
  mount();
}

if (typeof window !== 'undefined') {
  window.hubMountAssistant = mount;
  window.addEventListener('hub:assistant:mount', mount);
}
